export const getInteractiveToolBindingSkippedCount = (summary) => {
  const skippedBindings = Array.isArray(summary?.skipped_bindings) ? summary.skipped_bindings : [];
  return Number(summary?.skipped_count || skippedBindings.length || 0);
};

export const getActivationDowngradeCount = (report) => {
  const items = Array.isArray(report?.items) ? report.items : [];
  return Number(report?.downgraded_count || items.length || 0);
};

export const formatInteractiveToolBindingImportMessage = (
  summary,
  successPrefix = "Interactive tool bindings imported."
) => {
  const lines = [];
  for (const row of summary?.bindings || []) {
    if (row.state === "added") lines.push(`Tool binding ${row.binding_id}: added to the program.`);
    if (row.state === "differs_kept") lines.push(`Tool binding ${row.binding_id}: the program's kept; the bundle's copy differs.`);
  }
  for (const row of summary?.definitions || []) {
    const name = `Tool ${row.tool_key} version ${row.version}`;
    if (row.state === "added") lines.push(`${name}: added to this server.`);
    if (row.state === "differs_kept") lines.push(`${name}: this server's kept; the bundle's copy differs.`);
  }
  return [successPrefix, ...lines].filter(Boolean).join("\n");
};

export const formatActivationDowngradeReportMessage = (report) => {
  const items = Array.isArray(report?.items) ? report.items : [];
  const downgradedCount = getActivationDowngradeCount(report);

  if (downgradedCount <= 0) {
    return null;
  }

  const detailLines = items.map((item, index) => {
    const path = item?.path || item?.model || "Imported item";
    const language = item?.language ? ` (${item.language})` : "";
    const reasons = Array.isArray(item?.reasons) && item.reasons.length
      ? item.reasons : [item?.reason || "Not ready for learners."];
    return `  ${index + 1}. ${path}${language}\n     ${reasons.join("\n").replace(/\n/g, "\n     ")}`;
  });

  return [
    `Switched off, not ready for learners yet: ${downgradedCount}`,
    ...detailLines,
  ].join("\n");
};

export const formatCourseImportMessage = (
  interactiveToolBindingSummary,
  activationDowngradeReport,
  successPrefix = "Course imported."
) => {
  return [
    formatInteractiveToolBindingImportMessage(interactiveToolBindingSummary, successPrefix),
    formatActivationDowngradeReportMessage(activationDowngradeReport),
  ]
    .filter(Boolean)
    .join("\n");
};

export const formatPendingSourcesMessage = (pendingSourceRefs) => {
  if (!Array.isArray(pendingSourceRefs) || pendingSourceRefs.length === 0) return null;
  const lines = pendingSourceRefs.map(({ field, refs = [] }) => {
    const lessons = refs.filter((ref) => ref?.lesson).length;
    const scenarios = refs.filter((ref) => ref?.scenario).length;
    const sources = [
      lessons && `${lessons} lesson${lessons === 1 ? "" : "s"}`,
      scenarios && `${scenarios} role play${scenarios === 1 ? "" : "s"}`,
    ].filter(Boolean).join(" and ") || "the sources";
    return `  "${field}" stays off until ${sources} it reads are imported.`;
  });
  return [`Waiting for their sources: ${pendingSourceRefs.length}`, ...lines].join("\n");
};

export const formatImportAnswer = (data, successText = "Imported.") => [
  formatCourseImportMessage(data?.interactive_tool_binding_import, data?.activation_downgrade_report, successText),
  formatPendingSourcesMessage(data?.pending_source_refs),
].filter(Boolean).join("\n");

/**
 * The answer to an all-skills import: how many groups and skills it added and skipped, the skipped ones by name
 * (their names are in the course already), and a warning when it skipped any.
 */
export const skillsImportAllAnswer = (data) => {
  const summary = data?.summary || {};
  const count = (key) => Number(summary[key] || 0);
  const skipped = (label, names) => (Array.isArray(names) && names.length
    ? `${label} already in the course, skipped: ${names.join(", ")}` : null);
  const text = [
    `Skills imported. Groups: ${count("groups_imported")} added, ${count("groups_skipped")} skipped. `
      + `Skills: ${count("skills_imported")} added, ${count("skills_skipped")} skipped.`,
    skipped("Groups", data?.skipped_groups),
    skipped("Skills", data?.skipped_skills),
  ].filter(Boolean).join("\n");
  return {
    message: formatImportAnswer(data, text),
    status: count("groups_skipped") + count("skills_skipped") > 0 ? "warning" : "success",
  };
};
